import { useMemo, useState } from 'react'
import { ArrowRight, Combine, CornerDownRight } from 'lucide-react'
import { CARD_COLUMNS } from '../data'
import { charCount, mergeUnique, paragraphs, summaryFromContent } from '../lib/utils'
import { textSimilarity } from '../lib/ai'
import { ModalShell } from './Shared'

function combineTexts(cards) {
  const result = []
  cards.forEach((card) => {
    paragraphs(card.content).forEach((block) => {
      const isDuplicated = result.some((existing) => textSimilarity(existing, block) > 0.34)
      if (!isDuplicated) result.push(block)
    })
  })
  return result
}

export default function MergeModal({ cards, onClose, onMerge }) {
  const [title, setTitle] = useState(cards[0]?.title || '合稿')
  const [keepConnections, setKeepConnections] = useState(true)
  const [error, setError] = useState('')
  const blocks = useMemo(() => combineTexts(cards), [cards])
  const tags = mergeUnique(cards.flatMap((card) => card.tags || []))
  const combinedWords = charCount(blocks.join(' '))
  const sourceWords = cards.reduce((sum, card) => sum + charCount(card.content), 0)

  function submit() {
    if (!title.trim()) { setError('合并后的卡片需要一个标题。'); return }
    const content = blocks.join('\n\n')
    onMerge({
      title: title.trim(),
      content,
      tags,
      summary: summaryFromContent(content, cards[0]?.summary),
      keepConnections,
    })
  }

  return (
    <ModalShell onClose={onClose} width="830px" className="merge-modal">
      <div className="modal-topline"><span><Combine size={14} /> MERGE CARDS</span><em>把两张卡片，装订成同一个段落</em></div>
      <div className="merge-head">
        <div>
          <h2>合并 {cards.length} 张卡片。</h2>
          <p>合并会以第一张卡片为主卡，取走其余卡片的独有段落。重复的句子会被自动识别并跳过。</p>
        </div>
        <div className="merge-count"><strong>{cards.length}</strong><span>cards</span></div>
      </div>

      <div className="merge-sources">
        {cards.map((card, index) => (
          <div className="merge-source" key={card.id}>
            <span>{String(index + 1).padStart(2, '0')}</span>
            <div>
              <strong>{card.title}</strong>
              <em>{CARD_COLUMNS.find((column) => column.id === card.column)?.label} · {charCount(card.content)} 字</em>
            </div>
            {index < cards.length - 1 ? <ArrowRight size={14} /> : null}
          </div>
        ))}
      </div>

      <div className="merge-grid">
        <div className="merge-form">
          <label className="field-label"><span>合并后的标题</span><em>主卡标题</em></label>
          <input className="text-input text-input--title" value={title} aria-label="合并后的标题" onChange={(event) => { setTitle(event.target.value); setError('') }} autoFocus />
          <div className="merge-tags">
            <span>继承标签</span>
            {tags.map((tag) => <em key={tag}>#{tag}</em>)}
          </div>
          <label className="merge-check">
            <span className={'fake-check ' + (keepConnections ? 'is-checked' : '')} onClick={() => setKeepConnections(!keepConnections)}>{keepConnections ? '✓' : ''}</span>
            <span>
              <strong>保留并转移关系</strong>
              <em>其他卡片的关系会连到合并后的主卡上。</em>
            </span>
          </label>
          <div className="merge-hint">
            <CornerDownRight size={14} />
            <p>合并后仍可继续拆分。页屿不会删除任何原卡片内容，重复的段落会自动跳过。</p>
          </div>
        </div>

        <div className="merge-preview">
          <div className="merge-preview__head"><span>合并预览</span><em>{blocks.length} 段 · {combinedWords} 字</em></div>
          <div className="merge-paper">
            <div className="merge-paper__top"><span>MERGED CARD</span><span>保留 {cards[0]?.act}</span></div>
            <h3>{title || '未命名合稿'}</h3>
            <div className="merge-paper__body">
              {blocks.map((block, index) => <p key={index}>{block}</p>)}
            </div>
            <div className="merge-paper__foot">{tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          </div>
          {sourceWords > combinedWords ? (
            <div className="merge-saved">已自动去重约 {sourceWords - combinedWords} 字</div>
          ) : (
            <div className="merge-saved">保留了全部不重复内容</div>
          )}
        </div>
      </div>

      {error ? <div className="form-error merge-error">{error}</div> : null}
      <div className="modal-footer">
        <span className="modal-footer__hint">合并是一种结构动作，不会被自动保存到历史版本。</span>
        <div>
          <button className="btn btn--ghost" onClick={onClose}>取消</button>
          <button className="btn btn--ink" onClick={submit} disabled={!blocks.length}>确认合并 <Combine size={15} /></button>
        </div>
      </div>
    </ModalShell>
  )
}
